import { useContext, useRef } from "react";

import { FormType } from "@simplyk/common";

import { useFormV2Context } from "../contexts/FormV2Context";

import { getBuyerPrefillFromParticipants } from "./getBuyerPrefillFromParticipants";
import { usePrefillSearchParams } from "./usePrefillSearchParams";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";

type EditedField = "name" | "email";

export const useTrackPrefillEdited = () => {
  const context = useFormV2Context();
  const { logAmplitudeEvent } = useAmplitude();
  const { formData, formType, organization, formAmplitudeContext } = useContext(FrontendFormContext);
  const { contactInfo } = usePrefillSearchParams();
  const hasLoggedNameRef = useRef(false);
  const hasLoggedEmailRef = useRef(false);

  const log = (editedField: EditedField) => {
    logAmplitudeEvent(AmplitudeEvents.PaymentStepPrefillEdited, {
      formId: formData.id,
      formType,
      organizationCountry: organization?.country,
      editedField,
      ...formAmplitudeContext,
    });
  };

  const trackNameEdited = () => {
    if (context.type !== FormType.Ticketing || hasLoggedNameRef.current) {
      return;
    }
    const { form, questions } = context;
    const prefill = getBuyerPrefillFromParticipants(form.getValues("customAnswersOfParticipants"), questions);

    const firstNameEdited =
      Boolean(prefill.firstName) &&
      !contactInfo.firstName &&
      (form.getValues("firstName") ?? "").trim() !== prefill.firstName;
    const lastNameEdited =
      Boolean(prefill.lastName) &&
      !contactInfo.lastName &&
      (form.getValues("lastName") ?? "").trim() !== prefill.lastName;

    if (!firstNameEdited && !lastNameEdited) {
      return;
    }
    hasLoggedNameRef.current = true;
    log("name");
  };

  const trackEmailEdited = () => {
    if (context.type !== FormType.Ticketing || hasLoggedEmailRef.current) {
      return;
    }
    const { form, questions } = context;
    const prefill = getBuyerPrefillFromParticipants(form.getValues("customAnswersOfParticipants"), questions);

    const emailEdited =
      Boolean(prefill.email) && !contactInfo.email && (form.getValues("email") ?? "").trim() !== prefill.email;

    if (!emailEdited) {
      return;
    }
    hasLoggedEmailRef.current = true;
    log("email");
  };

  return { trackNameEdited, trackEmailEdited };
};
